import { useState } from 'react';
import { Check, Copy, CreditCard, QrCode } from 'lucide-react';
import type { MeioCobranca } from '@social/shared';
import { Botao, Campo, Entrada, Etiqueta } from '../componentes/ui';
import { Spinner } from '../componentes/carregando';
import { moeda } from './sociedade-comum';
import { ROTULO_COBRANCA, type Cobranca } from './assinatura-comum';

/**
 * O bloco de pagamento da tela de assinatura.
 *
 * ## Só aparece quando há o que pagar
 * Período isento, pago ou inexistente não desenha nada aqui. Um bloco
 * permanente de pagamento com "nada a pagar" ocuparia a altura mais nobre da
 * tela para não informar — e depois de uma semana ninguém mais o leria,
 * inclusive no mês em que houvesse cobrança.
 *
 * ## Faturamento antes do meio de pagamento
 * O CNPJ e o e-mail de cobrança aparecem PRIMEIRO quando faltam, e os botões
 * de pagar só depois. Descobrir que faltam dados só depois de clicar em "Pagar
 * com PIX" é o caminho mais longo até a informação — e o gateway recusa a
 * cobrança de pessoa jurídica sem documento de qualquer forma.
 */
export function BlocoDePagamento({
  cobranca,
  valorCentavos,
  disponivel,
  faturamentoCompleto,
  faturamento,
  podeCobrar,
  gerando,
  salvandoFaturamento,
  erro,
  aoGerar,
  aoSalvarFaturamento,
}: {
  cobranca: Cobranca | null;
  valorCentavos: number;
  /** O gateway está configurado? Ver `pagamentoDisponivel` no panorama. */
  disponivel: boolean;
  faturamentoCompleto: boolean;
  faturamento: { cnpj: string | null; emailCobranca: string | null };
  podeCobrar: boolean;
  gerando: MeioCobranca | null;
  salvandoFaturamento: boolean;
  erro: string | null;
  aoGerar: (meio: MeioCobranca) => void;
  aoSalvarFaturamento: (dados: { cnpj: string; emailCobranca: string }) => void;
}): JSX.Element {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-tinta-900 text-base font-semibold">Pagamento</h2>

      {erro && (
        <p
          role="alert"
          className="bg-erro-100 text-erro-600 rounded-lg px-4 py-3 text-sm"
        >
          {erro}
        </p>
      )}

      {/*
        Gateway não configurado: a tela DIZ isso, e não oferece um botão que
        devolveria um PIX de mentira. A pessoa copiaria o código, tentaria
        pagar e descobriria sozinha — é a "promessa falsa" da regra 6 aplicada
        a dinheiro.
      */}
      {!disponivel ? (
        <div className="border-tinta-100 bg-campo flex flex-col gap-2 rounded-xl border p-4">
          <p className="text-tinta-700 text-sm">
            Há {moeda(valorCentavos)} em aberto. O pagamento pelo sistema ainda não está
            disponível — combine com a Specializer como acertar este período.
          </p>
          <p className="text-tinta-500 text-sm">
            Seu acesso continua liberado enquanto isso.
          </p>
        </div>
      ) : !faturamentoCompleto ? (
        <FormularioFaturamento
          faturamento={faturamento}
          podeSalvar={podeCobrar}
          salvando={salvandoFaturamento}
          aoSalvar={aoSalvarFaturamento}
        />
      ) : cobranca && cobranca.situacao === 'AGUARDANDO' ? (
        <CobrancaAberta cobranca={cobranca} />
      ) : (
        <EscolhaDoMeio
          valorCentavos={valorCentavos}
          podeCobrar={podeCobrar}
          gerando={gerando}
          aoGerar={aoGerar}
        />
      )}
    </section>
  );
}

function FormularioFaturamento({
  faturamento,
  podeSalvar,
  salvando,
  aoSalvar,
}: {
  faturamento: { cnpj: string | null; emailCobranca: string | null };
  podeSalvar: boolean;
  salvando: boolean;
  aoSalvar: (dados: { cnpj: string; emailCobranca: string }) => void;
}): JSX.Element {
  const [cnpj, setCnpj] = useState(faturamento.cnpj ?? '');
  const [email, setEmail] = useState(faturamento.emailCobranca ?? '');

  return (
    <div className="border-tinta-100 bg-campo flex flex-col gap-3 rounded-xl border p-4">
      <p className="text-tinta-500 text-sm">
        Para emitir a cobrança, precisamos do CNPJ do escritório e de um e-mail para o
        comprovante. O e-mail pode ser diferente do seu de acesso.
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Campo
          rotulo="CNPJ do escritório"
          id="cnpjCobranca"
          obrigatorio
          dica="Só números"
        >
          <Entrada
            id="cnpjCobranca"
            inputMode="numeric"
            maxLength={14}
            value={cnpj}
            // Só dígitos entram no estado: a máscara é da tela, o dado é limpo.
            // Deixar a pontuação passar faria o gateway recusar o documento.
            onChange={(e) => setCnpj(e.target.value.replace(/\D/g, ''))}
          />
        </Campo>

        <Campo rotulo="E-mail de cobrança" id="emailCobranca" obrigatorio>
          <Entrada
            id="emailCobranca"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Campo>
      </div>

      <div>
        <Botao
          variante="secundaria"
          disabled={!podeCobrarCom(cnpj, email) || !podeSalvar || salvando}
          title={
            podeSalvar ? undefined : 'Só o administrador do escritório pode alterar.'
          }
          onClick={() => aoSalvar({ cnpj, emailCobranca: email })}
        >
          {salvando ? (
            <>
              <Spinner /> Salvando…
            </>
          ) : (
            'Salvar dados de faturamento'
          )}
        </Botao>
      </div>
    </div>
  );
}

/** 14 dígitos e um e-mail com arroba — o mesmo que o servidor exige. */
function podeCobrarCom(cnpj: string, email: string): boolean {
  return /^\d{14}$/.test(cnpj) && /.+@.+\..+/.test(email);
}

function EscolhaDoMeio({
  valorCentavos,
  podeCobrar,
  gerando,
  aoGerar,
}: {
  valorCentavos: number;
  podeCobrar: boolean;
  gerando: MeioCobranca | null;
  aoGerar: (meio: MeioCobranca) => void;
}): JSX.Element {
  return (
    <div className="border-tinta-100 bg-campo flex flex-col gap-3 rounded-xl border p-4">
      <p className="text-tinta-700 text-sm">
        Há {moeda(valorCentavos)} em aberto. Escolha como pagar:
      </p>

      <div className="flex flex-wrap gap-2">
        {(['PIX', 'CARTAO'] as const).map((meio) => (
          <Botao
            key={meio}
            variante="secundaria"
            disabled={!podeCobrar || gerando !== null}
            title={
              podeCobrar ? undefined : 'Só o administrador do escritório pode pagar.'
            }
            onClick={() => aoGerar(meio)}
          >
            {gerando === meio ? (
              <>
                <Spinner /> Gerando…
              </>
            ) : meio === 'PIX' ? (
              <>
                <QrCode aria-hidden className="size-4" />
                Pagar com PIX
              </>
            ) : (
              <>
                <CreditCard aria-hidden className="size-4" />
                Pagar com cartão
              </>
            )}
          </Botao>
        ))}
      </div>

      {/*
        A frase que evita o chamado mais previsível desta tela. O PIX não baixa
        na hora nem sempre, e sem este aviso a pessoa paga, atualiza, ainda vê
        "aguardando" e liga achando que o dinheiro sumiu.
      */}
      <p className="text-tinta-500 text-sm">
        A baixa é automática assim que o pagamento é confirmado — costuma levar alguns
        minutos no PIX.
      </p>
    </div>
  );
}

function CobrancaAberta({ cobranca }: { cobranca: Cobranca }): JSX.Element {
  const [copiado, setCopiado] = useState(false);

  const copiar = () => {
    if (!cobranca.pixCopiaECola) return;
    /*
      `navigator.clipboard` não existe em contexto inseguro nem em todo
      navegador — e a guarda pela metade (`typeof navigator !== 'undefined'`) é
      a armadilha nº 5 da skill. Sem o `?.`, o clique quebraria a tela inteira
      em vez de não copiar.
    */
    void navigator.clipboard?.writeText(cobranca.pixCopiaECola).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    });
  };

  return (
    <div className="border-tinta-100 bg-campo flex flex-col gap-3 rounded-xl border p-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <div>
          <span className="text-tinta-500 block text-xs">Situação</span>
          <Etiqueta tom="alerta">
            {ROTULO_COBRANCA[cobranca.situacao] ?? cobranca.situacao}
          </Etiqueta>
        </div>
        <div>
          <span className="text-tinta-500 block text-xs">Valor</span>
          <span className="text-tinta-900 text-sm font-medium tabular-nums">
            {moeda(cobranca.valorCentavos)}
          </span>
        </div>
        {cobranca.venceEm && (
          <div>
            <span className="text-tinta-500 block text-xs">Vence em</span>
            <span className="text-tinta-900 text-sm font-medium tabular-nums">
              {dataCurta(cobranca.venceEm)}
            </span>
          </div>
        )}
      </div>

      {cobranca.pixCopiaECola && (
        <div className="flex flex-col gap-2">
          <span className="text-tinta-500 text-xs">PIX copia e cola</span>
          <div className="flex flex-wrap items-center gap-2">
            <code className="border-tinta-100 bg-superficie text-tinta-700 min-w-0 flex-1 overflow-x-auto rounded-lg border px-3 py-2 text-xs">
              {cobranca.pixCopiaECola}
            </code>
            <Botao variante="secundaria" onClick={copiar}>
              {copiado ? (
                <>
                  <Check aria-hidden className="size-4" />
                  Copiado
                </>
              ) : (
                <>
                  <Copy aria-hidden className="size-4" />
                  Copiar
                </>
              )}
            </Botao>
          </div>
        </div>
      )}

      {cobranca.urlPagamento && (
        <div>
          <a
            href={cobranca.urlPagamento}
            target="_blank"
            rel="noreferrer"
            className="border-tinta-100 bg-superficie text-tinta-900 hover:border-marca-600 hover:bg-marca-600/10 inline-flex h-11 items-center gap-2 rounded-lg border px-3 text-sm transition-colors"
          >
            <CreditCard aria-hidden className="size-4" />
            Abrir pagamento
          </a>
        </div>
      )}

      <p className="text-tinta-500 text-sm">
        A baixa é automática assim que o pagamento é confirmado.
      </p>
    </div>
  );
}

/** `2026-08-30T…` → `30/08/2026`. */
function dataCurta(iso: string): string {
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  return `${dia ?? ''}/${mes ?? ''}/${ano ?? ''}`;
}
